import { ArrowLeft, ArrowUpRight, BarChart3, Bell, Bold, BookmarkPlus, CheckCircle, ChevronLeft, ChevronRight, Circle as CircleIcon, Clock3, Crop, Download, Euro, Eye, EyeOff, FileText, Folder, GitBranch, GitMerge, History, ImagePlus, Italic, MessageSquare, Pencil, Plus, RefreshCw, RotateCcw, RotateCw, Search, Sparkles, Square, Trash2, Type, Undo2, Upload, Users, Wand2, X, Zap } from "lucide-react";
import katex from "katex";
import "katex/dist/katex.min.css";
import { Link, useParams } from "react-router-dom";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { api, streamGuttenbergEvents } from "./api";
import { useAuth } from "../Auth";
import "./Guttenberg.css";
import { FullReportPreview } from "./GuttenbergReportPreview";
import { IMAGE_EDITOR_COLORS, annotationHasSize, applyCropDrag, clamp, createAnnotationId, createRotatedImageObjectUrl, fitCropToAspect, normalizeImageRotation, pathCaptionFallback, pointerPercentFromEvent, renderAnnotationSvg, renderEditedImageFile, reportImageSrc, rotateAnnotationPercent, rotateCropPercent } from "./GuttenbergImageAndDiff";
import { capitalizeCaption, formatDateTime } from "./GuttenbergChartMarkdown";
import { branchDepths, costOutcomeLabel, formatCostDuration, formatEuro, formatTokenCount, formatUsd, initials, locationLabel, mergeFeedbackDetail, mergeFeedbackTitle } from "./GuttenbergPageUtils";

function ReadOnlyReportPage() {
    const { projectId } = useParams();
    const [project, setProject] = useState(null);
    const [report, setReport] = useState(null);
    const [status, setStatus] = useState("Chargement du rapport...");

    const load = useCallback(async () => {
        setStatus("Chargement du rapport...");
        try {
            const [loadedProject, loadedReport] = await Promise.all([
                api.getProject(projectId),
                api.report(projectId).catch(() => null),
            ]);
            setProject(loadedProject);
            setReport(loadedReport);
            setStatus(loadedReport ? "" : "Aucune version redigee du rapport n'est encore disponible.");
        }
        catch (error) {
            setStatus(error?.message || "Impossible de charger le rapport.");
        }
    }, [projectId]);

    useEffect(() => {
        load();
    }, [load]);

    const backTarget = project?.deepflow_project_id
        ? `/project/${project.deepflow_project_id}`
        : "/tools/guttenberg";

    return (<main className="guttenberg-root shell wide">
      <div className="topbar">
        <div>
          <Link className="back-link" to={backTarget}>
            <ArrowLeft size={16}/> {project?.deepflow_project_id ? "Projet" : "Rapports"}
          </Link>
          <div className="brand">Innowide</div>
          <h1>{project?.title || report?.manifest?.title || "Rapport Guttenberg"}</h1>
          <div className="muted">Consultation du rapport</div>
        </div>
        <button className="button secondary" type="button" onClick={load}>
          <RefreshCw size={17}/> Rafraichir
        </button>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Lecture seule : vous pouvez consulter ce rapport, sans le modifier ni lancer d'operation Guttenberg.
      </div>

      {status && <section className="panel muted">{status}</section>}
      {report?.manifest?.sections?.length > 0 && (<div className="preview-editor single">
        <FullReportPreview
          report={report}
          sections={report.sections || {}}
          projectId={projectId}
          commentMode={false}
          editMode={false}
          comments={[]}
          sectionVersions={[]}
          activeVersion={null}
          processingCommentIds={{}}
          processingImageKeys={{}}
          processingSectionIds={{}}
          savingSectionIds={{}}
          selectedBankImage={null}
          collaborationLocks={[]}
          currentSessionId=""
        />
      </div>)}
    </main>);
}

function CollaborationStatus({ participants, activeBranch, locks }) {
    const aiLockCount = locks.filter((lock) => lock.kind === "ai").length;
    return (<div className="collaboration-status">
      <div className="branch-chip" title="Branche active">
        <GitBranch size={15}/>
        <span>{activeBranch?.name ?? "main"}</span>
      </div>
      <div className="presence-stack" title={`${participants.length} collaborateur(s) connecte(s)`}>
        <Users size={15}/>
        {participants.slice(0, 4).map((participant) => (<span className="presence-avatar" key={participant.session_id} style={{ background: participant.color }} title={`${participant.display_name} - ${locationLabel(participant)}`}>
            {initials(participant.display_name)}
          </span>))}
        {!participants.length && <span className="presence-empty">Solo</span>}
        {participants.length > 4 && <span className="presence-more">+{participants.length - 4}</span>}
      </div>
      {!!aiLockCount && <span className="ai-lock-chip">{aiLockCount} IA</span>}
    </div>);
}

function BranchPanel({ state, activeBranchId = "main", branchName, setBranchName, busy, mergeFeedback, onCreateBranch, onSwitchBranch, onMergeBranch, onClearMergeFeedback }) {
    const branches = state?.branches ?? [];
    const depthById = branchDepths(branches);
    if (!branches.length) {
        return <div className="empty-comments muted">Les branches apparaitront apres generation du rapport.</div>;
    }
    return (<div className="branch-panel">
      <div className="branch-panel-head">
        <strong>Branches du rapport</strong>
        <span>{branches.length} branche(s)</span>
      </div>
      <div className="branch-form">
        <input value={branchName} onChange={(event) => setBranchName(event.target.value)} placeholder="Nom de branche"/>
        <button className="button secondary compact-button" type="button" disabled={busy || !branchName.trim()} onClick={onCreateBranch}>
          <GitBranch size={15}/> Creer
        </button>
      </div>
      {mergeFeedback && (<div className={`merge-feedback ${mergeFeedback.status}`}>
        <div className="merge-feedback-main">
          <GitMerge size={16}/>
          <div>
            <strong>{mergeFeedbackTitle(mergeFeedback)}</strong>
            <span>{mergeFeedbackDetail(mergeFeedback)}</span>
          </div>
        </div>
        {!!mergeFeedback.conflicts?.length && (<ul>
          {mergeFeedback.conflicts.slice(0, 4).map((conflict) => (<li key={conflict.section_id}>{conflict.title || conflict.section_id}</li>))}
          {mergeFeedback.conflicts.length > 4 && <li>{mergeFeedback.conflicts.length - 4} autre(s) section(s)</li>}
        </ul>)}
        <div className="merge-feedback-actions">
          {mergeFeedback.status === "conflict" && (<button className="button secondary compact-button force-merge-button" type="button" disabled={busy} onClick={() => onMergeBranch(mergeFeedback.sourceBranchId, "source")}>
            Forcer le merge
          </button>)}
          <button className="button secondary icon-button" type="button" onClick={onClearMergeFeedback} title="Masquer">
            <X size={14}/>
          </button>
        </div>
      </div>)}
      <div className="branch-graph">
        {branches.map((branch) => {
            const active = branch.id === activeBranchId;
            const depth = depthById.get(branch.id) ?? 0;
            return (<div className={`branch-node ${active ? "active" : ""} ${branch.status}`} key={branch.id} style={{ marginLeft: `${Math.min(depth, 4) * 18}px` }}>
            <span className="branch-line"/>
            <div className="branch-dot"/>
            <div className="branch-node-main">
              <strong>{branch.name}</strong>
              <small>
                {branch.version_count} version(s)
                {branch.status === "merged" ? " - mergee" : ""}
              </small>
            </div>
            <div className="branch-node-actions">
              <button className="button secondary icon-button" type="button" disabled={busy || active} onClick={() => onSwitchBranch(branch.id)} title="Basculer sur cette branche">
                <GitBranch size={14}/>
              </button>
              <button className="button secondary icon-button" type="button" disabled={busy || active} onClick={() => onMergeBranch(branch.id, "safe")} title="Merger dans la branche active">
                <GitMerge size={14}/>
              </button>
              <button className="button secondary compact-button force-merge-button" type="button" disabled={busy || active} onClick={() => onMergeBranch(branch.id, "source")} title="Forcer le merge en gardant les sections de cette branche">
                Forcer
              </button>
            </div>
          </div>);
        })}
      </div>
    </div>);
}

function ImageCropModal({ projectId, imagePath, caption, busy, onApply, onClose }) {
    const imageRef = useRef(null);
    const dragRef = useRef(null);
    const annotationDragRef = useRef(null);
    const workingImageUrlRef = useRef("");
    const imageSrc = reportImageSrc(projectId, imagePath);
    const [crop, setCrop] = useState({ x: 0, y: 0, width: 100, height: 100 });
    const [aspect, setAspect] = useState("free");
    const [tool, setTool] = useState("crop");
    const [annotations, setAnnotations] = useState([]);
    const [draftAnnotation, setDraftAnnotation] = useState(null);
    const [annotationColor, setAnnotationColor] = useState("#d92d20");
    const [annotationStrokeWidth, setAnnotationStrokeWidth] = useState(0.7);
    const [annotationText, setAnnotationText] = useState("Texte");
    const [annotationTextSize, setAnnotationTextSize] = useState(5);
    const [imageSize, setImageSize] = useState(null);
    const [outputWidth, setOutputWidth] = useState(0);
    const [captionDraft, setCaptionDraft] = useState(capitalizeCaption(caption || pathCaptionFallback(imagePath)));
    const [error, setError] = useState("");
    const [rotation, setRotation] = useState(0);
    const [rotationBusy, setRotationBusy] = useState(false);
    const [workingImageSrc, setWorkingImageSrc] = useState(imageSrc);
    const visibleAnnotations = draftAnnotation ? [...annotations, draftAnnotation] : annotations;
    const cropPixels = imageSize
        ? {
            width: Math.max(1, Math.round((crop.width / 100) * imageSize.width)),
            height: Math.max(1, Math.round((crop.height / 100) * imageSize.height))
        }
        : null;
    const outputHeight = cropPixels && outputWidth ? Math.round(outputWidth * (cropPixels.height / cropPixels.width)) : 0;
    useEffect(() => () => {
        if (workingImageUrlRef.current)
            URL.revokeObjectURL(workingImageUrlRef.current);
    }, []);
    function handleImageLoad() {
        const image = imageRef.current;
        if (!image)
            return;
        const nextSize = { width: image.naturalWidth, height: image.naturalHeight };
        setImageSize(nextSize);
        setOutputWidth(clamp(nextSize.width, 320, 4000));
    }
    function updateAspect(nextAspect) {
        setAspect(nextAspect);
        setCrop((current) => fitCropToAspect(current, nextAspect, imageSize));
        setTool("crop");
    }
    function resetEditor() {
        if (workingImageUrlRef.current) {
            URL.revokeObjectURL(workingImageUrlRef.current);
            workingImageUrlRef.current = "";
        }
        setWorkingImageSrc(imageSrc);
        setRotation(0);
        setCrop({ x: 0, y: 0, width: 100, height: 100 });
        setAspect("free");
        setAnnotations([]);
        setDraftAnnotation(null);
        setTool("crop");
        if (imageSize)
            setOutputWidth(clamp(imageSize.width, 320, 4000));
    }
    async function rotateImage(delta) {
        if (rotationBusy)
            return;
        setRotationBusy(true);
        setError("");
        try {
            const nextRotation = normalizeImageRotation(rotation + delta);
            const nextUrl = nextRotation ? await createRotatedImageObjectUrl(imageSrc, nextRotation) : "";
            if (workingImageUrlRef.current)
                URL.revokeObjectURL(workingImageUrlRef.current);
            workingImageUrlRef.current = nextUrl;
            setImageSize(null);
            setWorkingImageSrc(nextUrl || imageSrc);
            setRotation(nextRotation);
            setCrop((current) => rotateCropPercent(current, delta));
            setAnnotations((current) => current.map((annotation) => rotateAnnotationPercent(annotation, delta)));
            setDraftAnnotation(null);
            setAspect("free");
            setTool("crop");
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Rotation de l'image impossible.");
        }
        finally {
            setRotationBusy(false);
        }
    }
    function startDrag(mode, event) {
        event.preventDefault();
        event.stopPropagation();
        dragRef.current = { mode, startX: event.clientX, startY: event.clientY, startCrop: crop };
        event.currentTarget.setPointerCapture(event.pointerId);
    }
    function dragCrop(event) {
        const drag = dragRef.current;
        const image = imageRef.current;
        if (!drag || !image)
            return;
        const rect = image.getBoundingClientRect();
        const dx = ((event.clientX - drag.startX) / rect.width) * 100;
        const dy = ((event.clientY - drag.startY) / rect.height) * 100;
        setCrop(applyCropDrag(drag.startCrop, drag.mode, dx, dy, aspect, imageSize));
    }
    function startAnnotation(event) {
        if (tool === "crop" || !imageSize || event.button !== 0)
            return;
        const point = pointerPercentFromEvent(event, imageRef.current);
        if (!point)
            return;
        event.preventDefault();
        event.stopPropagation();
        if (tool === "text") {
            const text = annotationText.trim() || "Texte";
            setAnnotations((current) => [
                ...current,
                {
                    id: createAnnotationId(),
                    type: "text",
                    x: point.x,
                    y: point.y,
                    text,
                    color: annotationColor,
                    fontSize: annotationTextSize
                }
            ]);
            return;
        }
        const annotation = {
            id: createAnnotationId(),
            type: tool,
            x1: point.x,
            y1: point.y,
            x2: point.x,
            y2: point.y,
            color: annotationColor,
            strokeWidth: annotationStrokeWidth
        };
        annotationDragRef.current = { pointerId: event.pointerId, annotation };
        setDraftAnnotation(annotation);
        try {
            event.currentTarget.setPointerCapture(event.pointerId);
        }
        catch {
            // Pointer capture is best effort for annotation drawing.
        }
    }
    function dragAnnotation(event) {
        const drag = annotationDragRef.current;
        if (!drag)
            return false;
        const point = pointerPercentFromEvent(event, imageRef.current);
        if (!point)
            return true;
        setDraftAnnotation({ ...drag.annotation, x2: point.x, y2: point.y });
        return true;
    }
    function endAnnotation(event) {
        const drag = annotationDragRef.current;
        if (!drag)
            return false;
        const point = pointerPercentFromEvent(event, imageRef.current);
        const annotation = point ? { ...drag.annotation, x2: point.x, y2: point.y } : draftAnnotation;
        if (annotation && annotationHasSize(annotation)) {
            setAnnotations((current) => [...current, annotation]);
        }
        setDraftAnnotation(null);
        annotationDragRef.current = null;
        try {
            event.currentTarget.releasePointerCapture(drag.pointerId);
        }
        catch {
            // The browser may already have released pointer capture.
        }
        return true;
    }
    function handlePointerMove(event) {
        if (dragAnnotation(event))
            return;
        dragCrop(event);
    }
    function handlePointerEnd(event) {
        if (endAnnotation(event))
            return;
        endDrag(event);
    }
    function endDrag(event) {
        if (dragRef.current) {
            try {
                event.currentTarget.releasePointerCapture(event.pointerId);
            }
            catch {
                // Pointer capture may belong to a nested handle; the browser releases it on pointerup.
            }
            dragRef.current = null;
        }
    }
    async function applyCrop() {
        if (!imageSize)
            return;
        setError("");
        try {
            const file = await renderEditedImageFile(workingImageSrc, imagePath, crop, imageSize, outputWidth || cropPixels?.width || 1000, annotations);
            await onApply(file, capitalizeCaption(captionDraft || pathCaptionFallback(imagePath)));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : "Edition image impossible.");
        }
    }
    function undoAnnotation() {
        setAnnotations((current) => current.slice(0, -1));
    }
    return (<div className="image-editor-backdrop" role="dialog" aria-modal="true">
      <div className="image-editor-modal">
        <div className="image-editor-head">
          <div>
            <span>Image</span>
            <h2>Editer l'image</h2>
          </div>
          <button className="button secondary icon-button" type="button" onClick={onClose} title="Fermer">
            <X size={16}/>
          </button>
        </div>
        <div className="image-editor-body">
          <div className="crop-stage">
            <div className={`crop-image-wrap image-tool-${tool}`} onPointerDown={startAnnotation} onPointerMove={handlePointerMove} onPointerUp={handlePointerEnd} onPointerCancel={handlePointerEnd}>
              <img ref={imageRef} crossOrigin="anonymous" draggable="false" src={workingImageSrc} alt={captionDraft} onLoad={handleImageLoad}/>
              {imageSize && (<svg className="annotation-layer" viewBox={`0 0 ${imageSize.width} ${imageSize.height}`} preserveAspectRatio="none" style={{ pointerEvents: tool === "crop" ? "none" : "auto" }}>
                  {visibleAnnotations.map((annotation) => renderAnnotationSvg(annotation, imageSize))}
                </svg>)}
              {tool === "crop" && (<div className="crop-box" style={{
            left: `${crop.x}%`,
            top: `${crop.y}%`,
            width: `${crop.width}%`,
            height: `${crop.height}%`
        }} onPointerDown={(event) => startDrag("move", event)}>
                {["nw", "ne", "sw", "se"].map((handle) => (<span className={`crop-handle ${handle}`} key={handle} onPointerDown={(event) => startDrag(handle, event)}/>))}
              </div>)}
            </div>
          </div>
          <div className="image-editor-controls">
            <label>
              Legende
              <input value={captionDraft} onChange={(event) => setCaptionDraft(event.target.value)}/>
            </label>
            <div className="control-group">
              <span>Rotation</span>
              <div className="segmented-control image-rotation-control">
                <button disabled={rotationBusy} onClick={() => rotateImage(-90)} title="Pivoter de 90 degres vers la gauche" type="button">
                  <RotateCcw size={15}/> Gauche
                </button>
                <button disabled={rotationBusy} onClick={() => rotateImage(90)} title="Pivoter de 90 degres vers la droite" type="button">
                  <RotateCw size={15}/> Droite
                </button>
                <strong>{rotation === 270 ? "-90" : rotation}°</strong>
              </div>
            </div>
            <div className="control-group">
              <span>Outil</span>
              <div className="segmented-control image-tool-control">
                {[
            ["crop", "Crop", Crop],
            ["rect", "Rect", Square],
            ["ellipse", "Ellipse", CircleIcon],
            ["arrow", "Fleche", ArrowUpRight],
            ["text", "Texte", Type]
        ].map(([value, label, Icon]) => (<button className={tool === value ? "active" : ""} key={value} onClick={() => setTool(value)} title={label} type="button">
                    <Icon size={14}/>
                    <span>{label}</span>
                  </button>))}
              </div>
            </div>
            <div className="control-group">
              <span>Ratio</span>
              <div className="segmented-control">
                {[
            ["free", "Libre"],
            ["1:1", "1:1"],
            ["4:3", "4:3"],
            ["16:9", "16:9"]
        ].map(([value, label]) => (<button className={aspect === value ? "active" : ""} key={value} onClick={() => updateAspect(value)} type="button">
                    {label}
                  </button>))}
              </div>
            </div>
            <label>
              Largeur de sortie
              <input min={120} max={4000} step={10} type="number" value={outputWidth || ""} onChange={(event) => setOutputWidth(clamp(Number(event.target.value) || 0, 120, 4000))}/>
            </label>
            {tool !== "crop" && (<>
                <div className="control-group">
                  <span>Couleur</span>
                  <div className="color-swatches">
                    {IMAGE_EDITOR_COLORS.map((color) => (<button aria-label={`Couleur ${color}`} className={annotationColor === color ? "active" : ""} key={color} onClick={() => setAnnotationColor(color)} style={{ background: color }} type="button"/>))}
                    <input aria-label="Couleur personnalisee" type="color" value={annotationColor} onChange={(event) => setAnnotationColor(event.target.value)}/>
                  </div>
                </div>
                {tool !== "text" && (<label>
                    Epaisseur
                    <input min={0.2} max={3} step={0.1} type="range" value={annotationStrokeWidth} onChange={(event) => setAnnotationStrokeWidth(Number(event.target.value) || 0.7)}/>
                  </label>)}
                {tool === "text" && (<>
                    <label>
                      Texte a poser
                      <input value={annotationText} onChange={(event) => setAnnotationText(event.target.value)}/>
                    </label>
                    <label>
                      Taille texte
                      <input min={2} max={12} step={0.5} type="range" value={annotationTextSize} onChange={(event) => setAnnotationTextSize(Number(event.target.value) || 5)}/>
                    </label>
                  </>)}
                <div className="annotation-actions">
                  <button className="button secondary compact-button" type="button" onClick={undoAnnotation} disabled={!annotations.length}>
                    <Undo2 size={14}/> Annuler trait
                  </button>
                  <button className="button secondary compact-button" type="button" onClick={() => setAnnotations([])} disabled={!annotations.length}>
                    <Trash2 size={14}/> Effacer
                  </button>
                </div>
              </>)}
            <div className="crop-readout">
              <span>Source: {cropPixels ? `${cropPixels.width} x ${cropPixels.height}px` : "..."}</span>
              <span>Sortie: {outputWidth && outputHeight ? `${outputWidth} x ${outputHeight}px` : "..."}</span>
              <span>Rotation: {rotation === 270 ? "-90" : rotation}°</span>
              <span>Annotations: {annotations.length}</span>
            </div>
            {error && <div className="form-error">{error}</div>}
            <div className="image-editor-actions">
              <button className="button secondary compact-button" type="button" onClick={resetEditor}>
                Reinitialiser
              </button>
              <button className="button secondary compact-button" type="button" onClick={onClose}>
                Annuler
              </button>
              <button className="button compact-button" type="button" disabled={busy || rotationBusy || !imageSize} onClick={applyCrop}>
                <Pencil size={15}/> Appliquer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>);
}

function JobTracePanel({ trace }) {
    if (!trace.length)
        return null;
    const visible = trace.slice(-8).reverse();
    return (<div className="job-trace" aria-label="Deroule operationnel du job">
      <div className="job-trace-head">
        <strong>Deroule</strong>
        <span>Etapes visibles, sans raisonnement prive</span>
      </div>
      <ol>
        {visible.map((item, index) => (<li className={item.status ?? "running"} key={`${item.created_at ?? index}-${item.title}`}>
            <span className="job-trace-dot"/>
            <div>
              <strong>{item.title}</strong>
              {item.message ? <p>{item.message}</p> : null}
            </div>
          </li>))}
      </ol>
    </div>);
}

function AiActivityPanel({ job, jobInProgress, progress, status, trace, activityDetail, onCancel }) {
    if (!job && !trace.length)
        return null;
    const hasProgressTotal = Boolean(job?.progress_total);
    const progressLabel = hasProgressTotal ? `${job.progress_current}/${job.progress_total}` : job?.status ?? "";
    return (<section className="panel ai-activity-panel">
      <div className="section-heading">
        <h2>Activite IA</h2>
        {jobInProgress && (<button className="button secondary compact-button stop-job-button" onClick={onCancel} type="button">
            <X size={15}/> Arreter
          </button>)}
      </div>
      {job ? (<div className="progress-wrap">
          <div className="progress-meta">
            <span>{job.message}</span>
            <span>{progressLabel}</span>
          </div>
          {job.current_step && <div className="ai-current-step">{job.current_step}</div>}
          {jobInProgress && activityDetail && <div className="ai-activity-detail">{activityDetail}</div>}
          <div className={`progress ${hasProgressTotal ? "" : "indeterminate"}`}><span style={{ width: `${hasProgressTotal ? progress : 35}%` }}/></div>
        </div>) : status ? (<p className="status">{status}</p>) : null}
      <JobTracePanel trace={trace}/>
    </section>);
}

function WorkflowStepper({ steps, busy, status, job, progress, activityDetail, collapsed, onToggleCollapsed, onUploadFiles, onCancel }) {
    const activeStep = steps.find((step) => step.status === "running") ?? steps.find((step) => step.status === "active" || step.status === "warning") ?? steps[0];
    const activeJobRunning = job && (job.status === "queued" || job.status === "running");
    const hasProgressTotal = Boolean(job?.progress_total);
    const jobMessage = String(job?.message || "").trim();
    const currentStep = String(job?.current_step || "").trim();
    const activeDetail = activeJobRunning
        ? jobMessage || currentStep || activeStep.detail
        : activeStep.detail;
    const progressText = hasProgressTotal ? `${progress}%` : "";
    const currentStatus = activeJobRunning
        ? [currentStep && currentStep !== jobMessage ? currentStep : "", progressText, activityDetail]
            .filter(Boolean)
            .join(" - ") || "Traitement en cours"
        : status || "Pret";
    const progressClass = `progress workflow-progress ${hasProgressTotal ? "" : "indeterminate"}`;
    const progressWidth = `${hasProgressTotal ? progress : 35}%`;
    if (collapsed) {
        return (<section className="workflow-panel workflow-panel-collapsed" aria-label="Workflow du rapport">
        <div className="workflow-head">
          <div>
            <strong>Etape: {activeStep.title.replace(/^\d+\.\s*/, "")}</strong>
            <span>{activeDetail}</span>
          </div>
          <div className="workflow-compact-actions">
            <div className="workflow-status" title={currentStatus}>{currentStatus}</div>
            {activeJobRunning && (<button className="workflow-button danger" onClick={onCancel} type="button">
              <X size={14}/> Arreter
            </button>)}
            <button className="workflow-button secondary" onClick={onToggleCollapsed} type="button">
              Afficher
            </button>
          </div>
        </div>
        {activeJobRunning && (<div className={progressClass}><span style={{ width: progressWidth }}/></div>)}
      </section>);
    }
    return (<section className="workflow-panel" aria-label="Workflow du rapport">
      <div className="workflow-head">
        <div>
          <strong>{activeJobRunning ? "Etape" : "Prochaine etape"}: {activeStep.title.replace(/^\d+\.\s*/, "")}</strong>
          <span>{activeDetail}</span>
        </div>
        <div className="workflow-compact-actions">
          <div className="workflow-status" title={currentStatus}>{currentStatus}</div>
          {activeJobRunning && (<button className="workflow-button danger" onClick={onCancel} type="button">
            <X size={14}/> Arreter
          </button>)}
          <button className="workflow-button secondary" onClick={onToggleCollapsed} type="button">
            Masquer
          </button>
        </div>
      </div>
      {activeJobRunning && (<div className={progressClass}><span style={{ width: progressWidth }}/></div>)}
      <div className="workflow-steps">
        {steps.map((step) => (<div className={`workflow-step ${step.status}`} key={step.id} aria-busy={step.status === "running"}>
            <div className="workflow-step-main">
              {step.status === "running" ? <span className="workflow-spinner" aria-hidden="true"/> : <span className="workflow-dot"/>}
              <div>
                <strong>{step.title}</strong>
                <p>{step.detail}</p>
              </div>
            </div>
            <div className="workflow-actions">
              {step.id === "sources" ? (<label className={`workflow-button secondary ${busy ? "disabled" : ""}`}>
                  <Upload size={14}/>
                  {step.action?.label ?? "Ajouter"}
                  <input disabled={busy} hidden type="file" multiple onChange={onUploadFiles}/>
                </label>) : step.action ? (<button className={`workflow-button ${step.action.kind === "secondary" ? "secondary" : ""}`} disabled={step.action.disabled} onClick={step.action.onClick} title={step.action.title} type="button">
                  {step.action.label}
                </button>) : null}
              {step.secondaryAction && (<button className={`workflow-button ${step.secondaryAction.kind === "primary" ? "" : "secondary"}`} disabled={step.secondaryAction.disabled} onClick={step.secondaryAction.onClick} title={step.secondaryAction.title} type="button">
                  {step.secondaryAction.label}
                </button>)}
            </div>
          </div>))}
      </div>
    </section>);
}

function PlanReviewPanel({ title, sections, instruction, setInstruction, onRefine, onApprove, busy, approving = false }) {
    const totalPages = sections.reduce((sum, section) => sum + (section.estimated_pages ?? 0), 0);
    return (<article className="paper-preview outline-preview plan-review">
      <div className="paper-cover">
        <div className="brand">Innowide</div>
        <h1>{title}</h1>
        <div className="muted">Plan à relire avant rédaction</div>
      </div>
      <div className="plan-review-banner">
        <div>
          <strong>Validation du plan requise</strong>
          <span>La rédaction reste bloquée tant que ce plan n'est pas validé.</span>
        </div>
        <button className="button" disabled={busy || !sections.length} onClick={onApprove} type="button" aria-busy={approving}>
          {approving ? <span className="inline-spinner" aria-hidden="true"/> : <CheckCircle size={16}/>}
          {approving ? "Validation en cours..." : "Valider le plan"}
        </button>
      </div>
      <div className="outline-refine plan-refine">
        <input value={instruction} onChange={(event) => setInstruction(event.target.value)} placeholder="Demander une modification du plan avant validation"/>
        <button disabled={busy || !instruction.trim()} className="button secondary compact-button" onClick={onRefine} type="button">
          Modifier plan
        </button>
      </div>
      <h1>Plan du rapport</h1>
      <div className="outline-summary">
        {sections.length} section(s){totalPages > 0 ? ` - ${totalPages.toFixed(1)} page(s) estimee(s)` : ""}
      </div>
      {sections.map((section, index) => (<section className="outline-section" key={`${section.id}-${index}`}>
          <div className="outline-section-head">
            <h2>{section.title || `Section ${index + 1}`}</h2>
            {section.estimated_pages ? <span>{section.estimated_pages} p.</span> : null}
          </div>
          {(section.role || section.key_message) && (<div className="outline-section-meta">
              {section.role && <span>{section.role}</span>}
              {section.key_message && <span>{section.key_message}</span>}
            </div>)}
          {section.brief && <p>{section.brief}</p>}
          {!!section.subsections.length && (<ul>
              {section.subsections.map((item, itemIndex) => (<li key={`${section.id}-subsection-${itemIndex}`}>{item}</li>))}
            </ul>)}
          {!!section.content_blocks?.length && (<div className="outline-visual-requests">
              <strong>Blocs redactionnels</strong>
              <ul>
                {section.content_blocks.slice(0, 4).map((block, blockIndex) => (<li key={`${section.id}-block-${blockIndex}`}>
                    {block.block_title || block.editorial_role || "Bloc"}{block.intent ? ` - ${block.intent}` : ""}
                  </li>))}
              </ul>
            </div>)}
          {!!section.data_questions?.length && (<div className="outline-visual-requests">
              <strong>Donnees a exploiter</strong>
              <ul>
                {section.data_questions.slice(0, 4).map((item, itemIndex) => (<li key={`${section.id}-data-${itemIndex}`}>{item}</li>))}
              </ul>
            </div>)}
          {!!section.visual_requests?.length && (<div className="outline-visual-requests">
              <strong>Visuels prévus</strong>
              <ul>
                {section.visual_requests.map((request, requestIndex) => (<li key={`${section.id}-visual-${requestIndex}`}>
                    {request.purpose || request.message_to_support || "Besoin visuel"}
                    {request.preferred_assets?.length
                        ? ` — ${request.preferred_assets.map((asset) => asset.user_label || asset.proposed_caption || asset.caption || asset.title).join(", ")}`
                        : " — candidat à rechercher par le writer"}
                  </li>))}
              </ul>
            </div>)}
          {!!section.visual_slots?.length && (<div className="outline-visual-requests">
              <strong>Placement visuel blueprint</strong>
              <ul>
                {section.visual_slots.slice(0, 4).map((slot, slotIndex) => (<li key={`${section.id}-slot-${slotIndex}`}>
                    {slot.caption || slot.figure_path || slot.creation_hint || slot.intent || "Slot visuel"}{slot.status ? ` (${slot.status})` : ""}
                  </li>))}
              </ul>
            </div>)}
        </section>))}
    </article>);
}

function CostPanel({ costs }) {
    const maxOperationCost = Math.max(...(costs?.by_operation ?? []).map((item) => item.cost_eur), 0.000001);
    const operationLabels = new Map((costs?.by_operation ?? []).map((item) => [item.key, item.label]));
    const latestEvents = [...(costs?.events ?? [])].reverse().slice(0, 80);
    const measuredCoverage = Math.round((costs?.measured_coverage ?? 0) * 100);
    const cacheRate = Math.round((costs?.cache_rate ?? 0) * 100);
    const reworkCount = (costs?.failed_event_count ?? 0) + (costs?.invalid_output_event_count ?? 0) + (costs?.repair_event_count ?? 0);
    return (<div className="analysis-workspace cost-workspace">
      <section className="analysis-card wide-card">
        <div className="section-heading">
          <h3>Budget et efficacite</h3>
          <span className="muted">{costs ? `${costs.event_count} appel(s) journalise(s)` : "Aucune donnee"}</span>
        </div>
        <div className="cost-kpis">
          <div>
            <Euro size={18}/>
            <span>Cout estime</span>
            <strong>{formatEuro(costs?.total_eur ?? 0)}</strong>
          </div>
          <div>
            <BarChart3 size={18}/>
            <span>Couverture mesure</span>
            <strong>{measuredCoverage}%</strong>
          </div>
          <div>
            <span>Cache input</span>
            <strong>{cacheRate}%</strong>
          </div>
          <div>
            <span>Retravail</span>
            <strong>{reworkCount}</strong>
          </div>
          <div>
            <span>Temps cumule</span>
            <strong>{formatCostDuration(costs?.total_duration_ms ?? 0)}</strong>
          </div>
          <div>
            <span>Tokens in / out</span>
            <strong>{formatTokenCount(costs?.input_tokens ?? 0)} / {formatTokenCount(costs?.output_tokens ?? 0)}</strong>
          </div>
          <div>
            <span>Cout / 1k tokens out</span>
            <strong>{formatEuro(costs?.cost_per_1k_output_tokens_eur ?? 0)}</strong>
          </div>
        </div>
        <p className="muted">
          Estimation basee sur les usages renvoyes par les fournisseurs et le taux USD/EUR configure.
          Les montants non mesures ne sont pas inclus dans le total.
        </p>
      </section>

      {!!costs?.insights?.length && (<section className="analysis-card wide-card">
        <div className="section-heading">
          <h3>Points d'attention</h3>
          <span className="muted">Signaux automatiques, sans contenu des prompts</span>
        </div>
        <div className="cost-insights">
          {costs.insights.map((insight) => (<article className={insight.severity} key={insight.code}>
              <div>
                <strong>{insight.title}</strong>
                {insight.cost_eur > 0 && <span>{formatEuro(insight.cost_eur)}</span>}
              </div>
              <p>{insight.detail}</p>
            </article>))}
        </div>
      </section>)}

      <section className="analysis-card wide-card">
        <div className="section-heading">
          <h3>Cout par type de demande</h3>
          <span className="muted">{formatUsd(costs?.total_usd ?? 0)}</span>
        </div>
        <div className="cost-bars">
          {(costs?.by_operation ?? []).map((bucket) => (<article key={bucket.key}>
              <div className="cost-bar-head">
                <strong>{bucket.label}</strong>
                <span>{formatEuro(bucket.cost_eur)}</span>
              </div>
              <div className="cost-bar-track">
                <span style={{ width: `${Math.max(3, (bucket.cost_eur / maxOperationCost) * 100)}%` }}/>
              </div>
              <div className="cost-bar-meta">
                <span>{bucket.event_count} appel(s)</span>
                <span>{formatTokenCount(bucket.input_tokens)} in / {formatTokenCount(bucket.output_tokens)} out</span>
                {!!(bucket.failed_event_count || bucket.invalid_output_event_count || bucket.repair_event_count) && (<span className="cost-rework-label">
                    {bucket.failed_event_count} echec(s), {bucket.invalid_output_event_count} invalide(s), {bucket.repair_event_count} reparation(s)
                  </span>)}
              </div>
            </article>))}
          {!costs?.by_operation.length && <div className="muted">Les prochains appels IA apparaitront ici.</div>}
        </div>
      </section>

      <section className="analysis-card">
        <h3>Agents les plus couteux</h3>
        <ul className="cost-model-list">
          {(costs?.by_agent ?? []).slice(0, 12).map((bucket) => (<li key={bucket.key}>
              <strong>{bucket.label}</strong>
              <span>{formatEuro(bucket.cost_eur)} - {bucket.event_count} appel(s) - {formatCostDuration(bucket.duration_ms)}</span>
            </li>))}
          {!costs?.by_agent?.length && <li className="muted">Aucun agent facture pour l'instant.</li>}
        </ul>
      </section>

      <section className="analysis-card">
        <h3>Modeles</h3>
        <ul className="cost-model-list">
          {(costs?.by_model ?? []).map((bucket) => (<li key={bucket.key}>
              <strong>{bucket.label}</strong>
              <span>{formatEuro(bucket.cost_eur)} - {formatTokenCount(bucket.total_tokens)}</span>
            </li>))}
          {!costs?.by_model.length && <li className="muted">Aucun modele facture pour l'instant.</li>}
        </ul>
      </section>

      <section className="analysis-card wide-card">
        <div className="section-heading">
          <h3>Journal des appels</h3>
          <span className="muted">80 derniers evenements</span>
        </div>
        <div className="cost-history">
          {latestEvents.map((event) => (<article className={`cost-event-${event.outcome ?? "completed"}`} key={event.id}>
              <div>
                <strong>{operationLabels.get(event.operation_type) ?? event.operation_type}</strong>
                <span className={`cost-outcome ${event.outcome ?? "completed"}`}>{costOutcomeLabel(event.outcome)}</span>
              </div>
              <div>
                <span>{formatDateTime(event.created_at)} - {event.agent_name || event.request_kind}</span>
                <strong>{event.measured ? formatEuro(event.cost_eur) : "non mesure"}</strong>
              </div>
              <small>
                {event.provider} - {event.model || "modele inconnu"} - {event.request_kind}
              </small>
              <small>
                {formatTokenCount(event.input_tokens)} input / {formatTokenCount(event.output_tokens)} output
                {event.cached_input_tokens ? ` / ${formatTokenCount(event.cached_input_tokens)} cache` : ""}
                {event.image_count ? ` / ${event.image_count} image(s)` : ""}
                {event.duration_ms ? ` / ${formatCostDuration(event.duration_ms)}` : ""}
                {event.tool_calls ? ` / ${event.tool_calls} outil(s)` : ""}
                {event.output_chars ? ` / ${formatTokenCount(event.output_chars)} car.` : ""}
              </small>
              {event.metadata?.repair_of && <small className="cost-event-detail">Reparation de {event.metadata.repair_of}</small>}
              {event.metadata?.error_type && <small className="cost-event-detail">Erreur: {event.metadata.error_type}</small>}
            </article>))}
          {!latestEvents.length && <div className="muted">Aucune depense journalisee.</div>}
        </div>
      </section>
    </div>);
}

function DriveSourcePicker({ driveFiles, driveLoading, driveSelection, driveBreadcrumbs, driveQuery, selectedDriveCount, busy, onQueryChange, onSearch, onClearSearch, onRefresh, onOpenFolder, onBreadcrumb, onToggle, onImport, onLoadMore }) {
    const files = driveFiles?.files ?? [];
    return (<div className="drive-picker">
      <div className="section-heading">
        <h3>Drive projet</h3>
        <button className="button secondary compact-button" type="button" onClick={onRefresh} disabled={busy || driveLoading}>
          <RefreshCw size={14}/> {driveLoading ? "Chargement" : "Actualiser"}
        </button>
      </div>
      <form className="drive-search" onSubmit={onSearch}>
        <Search size={15}/>
        <input value={driveQuery} onChange={(event) => onQueryChange(event.target.value)} placeholder="Rechercher dans le Drive"/>
        {driveQuery && (<button className="icon-button compact-icon" type="button" onClick={onClearSearch} title="Effacer">
            <X size={14}/>
          </button>)}
      </form>
      {driveFiles?.status === "lu" ? (<>
          {!driveQuery && (<div className="drive-breadcrumbs">
              {driveBreadcrumbs.map((crumb, index) => (<button key={`${crumb.id || "root"}-${index}`} type="button" onClick={() => onBreadcrumb(index)} disabled={index === driveBreadcrumbs.length - 1}>
                  {crumb.name}
                </button>))}
            </div>)}
          <div className="muted">
            {driveFiles.mode === "search" ? `${files.length} resultat(s)` : `${files.length} element(s) dans ce dossier`}. Coche un fichier ou un dossier complet a ajouter en reference.
          </div>
          {selectedDriveCount > 0 && (<div className="drive-selection-action" role="status" aria-live="polite">
              <div className="drive-selection-copy">
                <strong>{selectedDriveCount} source(s) selectionnee(s), pas encore ajoutee(s)</strong>
                <span>Clique sur le bouton pour les ajouter effectivement au dossier Guttenberg.</span>
              </div>
              <button className="button" type="button" onClick={onImport} disabled={busy}>
                <Plus size={16}/> Ajouter maintenant
              </button>
            </div>)}
          <div className="drive-file-list">
            {files.map((file) => {
            const selected = !!driveSelection[file.id];
            const canSelect = !file.imported && (file.is_folder || file.syncable);
            return (<div className={`drive-file-row ${selected ? "selected" : ""} ${!canSelect ? "disabled" : ""}`} key={file.id}>
                <input type="checkbox" checked={selected} disabled={!canSelect || busy} onChange={() => onToggle(file)} title={file.is_folder ? "Ajouter tout le dossier" : "Ajouter ce fichier"}/>
                <button className="drive-file-open" type="button" onClick={() => file.is_folder ? onOpenFolder(file) : undefined} disabled={!file.is_folder}>
                  {file.is_folder ? <Folder size={16}/> : <FileText size={16}/>}
                  <span>
                    <strong>{file.name}</strong>
                    <small>{file.imported ? "Deja ajoute" : file.is_folder ? "Dossier" : file.syncable ? file.mime_type : "Type non importable"}</small>
                    {driveFiles.mode === "search" && file.path && file.path !== file.name && <small>{file.path}</small>}
                  </span>
                </button>
              </div>);
        })}
            {!files.length && <div className="muted">Aucun element Drive trouve.</div>}
          </div>
          {driveFiles.next_page_token && (<button className="button secondary compact-button" type="button" onClick={onLoadMore} disabled={busy || driveLoading}>
              Charger plus
            </button>)}
        </>) : (<div className="muted">{driveFiles?.reason || "Drive non disponible pour ce projet."}</div>)}
    </div>);
}

export {
    AiActivityPanel,
    BranchPanel,
    CollaborationStatus,
    CostPanel,
    DriveSourcePicker,
    ImageCropModal,
    JobTracePanel,
    PlanReviewPanel,
    ReadOnlyReportPage,
    WorkflowStepper
};
